import React, { useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useFetch } from "@/lib/useFetch";
import { usePageMeta } from "@/lib/usePageMeta";
import { useI18n } from "@/contexts/I18nContext";
import { GENRES } from "@/lib/constants";
import { AuthorAvatar } from "@/components/AuthorAvatar";
import { ListLayout, FilterChip } from "@/components/ListLayout";
import { ListPagination } from "@/components/ListPagination";
import { ErrorState } from "@/components/StateViews";

const PAGE_SIZE = 48;

export default function Authors() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const genre = params.get("genre") || "";
  const page = Math.max(1, parseInt(params.get("page") || "1", 10) || 1);

  const qs = new URLSearchParams({ page: String(page), limit: String(PAGE_SIZE) });
  if (genre) qs.set("genre", genre);
  const { status, data, reload } = useFetch(`/authors?${qs}`);
  const items = data?.items || [];
  const total = data?.total || 0;
  usePageMeta({ title: t("all_authors"), canonical: page > 1 ? `/authors?page=${page}` : "/authors" });
  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [genre, page]);

  const set = (next) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(next)) { if (v) p.set(k, v); else p.delete(k); }
    if (!("page" in next)) p.delete("page");
    setParams(p);
  };

  const filters = (
    <fieldset>
      <legend className="mb-2 font-semibold">{t("filter_genre")}</legend>
      <ul className="flex flex-wrap gap-2 md:flex-col md:gap-0.5" data-testid="author-genre-filter">
        <li><FilterChip active={!genre} onClick={() => set({ genre: "" })} testid="author-genre-all">{t("all")}</FilterChip></li>
        {GENRES.map((g) => (
          <li key={g}><FilterChip active={genre === g} onClick={() => set({ genre: g })} testid={`author-genre-${g}`}>{g}</FilterChip></li>
        ))}
      </ul>
    </fieldset>
  );

  return (
    <ListLayout
      title={t("all_authors")}
      count={status === "ok" ? t("count_authors").replace("{n}", total) : null}
      filters={filters}
      active={genre ? [{ key: "genre", label: genre }] : []} onRemove={() => set({ genre: "" })}
    >
      {status === "error" ? <ErrorState onRetry={reload} />
        : status === "ok" && items.length === 0 ? <p className="py-10 text-muted-foreground">{t("no_authors")}</p>
        : (
          <ul className="grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 lg:grid-cols-5" data-testid="authors-grid">
            {status !== "ok"
              ? Array.from({ length: 10 }, (_, i) => <li key={i}><div className="mx-auto aspect-square w-24 rounded-full skel" /></li>)
              : items.map((a) => (
                <li key={a.id}>
                  <Link to={`/authors/${a.slug}`} className="group flex flex-col items-center text-center" data-testid={`author-${a.slug}`}>
                    <AuthorAvatar src={a.photo} name={a.name_hi} className="w-24" />
                    <span className="mt-2 leading-snug line-clamp-2 group-hover:text-primary">
                      {lang === "hi" ? a.name_hi : (a.name_en || a.name_hi)}
                    </span>
                  </Link>
                </li>
              ))}
          </ul>
        )}
      {status === "ok" && <ListPagination page={page} limit={PAGE_SIZE} total={total} onPageChange={(p) => set({ page: p > 1 ? String(p) : "" })} testid="authors-pagination" />}
    </ListLayout>
  );
}
